import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useFetchMenu from '../../Hooks/UseFetchMenu';
import { API_BASE_URL } from '../../config';
import {
  getFlavorBars,
  getWinePosition,
  getWineType,
  WINE_TYPE_FILTERS,
} from '../../utils/wineFlavorMap';
import styles from './WineFlavorMap.module.css';
import { formatPrice } from '../../utils/formatPrice';
import SmallNavBar from '../SmallNavBar/SmallNavBar';

const TYPE_BADGE_CLASS = {
  blanco: styles.badgeBlanco,
  orange: styles.badgeOrange,
  tinto: styles.badgeTinto,
  rosado: styles.badgeRosado,
  petnat: styles.badgePetnat,
};

const TYPE_LABEL = {
  blanco: 'BLANCO',
  orange: 'ORANGE',
  tinto: 'TINTO',
  rosado: 'ROSADO',
  petnat: 'PET NAT',
};

// Presets de "match rapido": cada uno es una condicion sobre datos reales
// del vino (no texto inventado). "all" no filtra nada.
const QUICK_MATCHES = [
  { id: 'all', label: 'Todos los vinos' },
  { id: 'salvaje', label: '⚡ Quiero algo salvaje', test: (w) => w.funkiness >= 3 },
  { id: 'fresco', label: '🍋 Fresco & crujiente', test: (w) => w.acidity >= 4 },
  { id: 'maridaje', label: '🍽️ Con maridaje sugerido', test: (w) => !!w.pairingSuggestion },
];

const bucket4 = (value0to100) => {
  if (value0to100 >= 75) return 4;
  if (value0to100 >= 50) return 3;
  if (value0to100 >= 25) return 2;
  return 1;
};

const bodyLabel = (level) => {
  if (level >= 4) return 'Denso / con cuerpo';
  if (level === 3) return 'Medio';
  if (level === 2) return 'Ligero & vibrante';
  return 'Muy ligero';
};

const WineFlavorMap = () => {
  const { id } = useParams();
  const { menuItems, loading, error } = useFetchMenu(API_BASE_URL);
  const [typeFilter, setTypeFilter] = useState('all');
  const [quickMatch, setQuickMatch] = useState('all');
  const [minFunk, setMinFunk] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const carouselRef = useRef(null);

  const category = menuItems.find((cat) => String(cat.id) === String(id));

  const wines = useMemo(() => {
    const products = category?.products || [];
    return products.map((item) => {
      const type = getWineType(item);
      const { acidity, intensity } = getWinePosition(item);
      const { aromas, funkiness } = getFlavorBars(item);
      return { ...item, type, acidity, intensity, aromas, funkiness };
    });
  }, [category]);

  const visibleWines = useMemo(() => {
    return wines.filter((wine) => {
      if (typeFilter !== 'all' && wine.type !== typeFilter) return false;
      if (wine.funkiness < minFunk) return false;
      const preset = QUICK_MATCHES.find((m) => m.id === quickMatch);
      if (preset && preset.test && !preset.test(wine)) return false;
      return true;
    });
  }, [wines, typeFilter, minFunk, quickMatch]);

  useEffect(() => {
    if (!visibleWines.length) {
      setSelectedId(null);
      return;
    }
    if (!visibleWines.some((wine) => wine.id === selectedId)) {
      setSelectedId(visibleWines[0].id);
    }
  }, [visibleWines, selectedId]);

  const selectedWine = visibleWines.find((wine) => wine.id === selectedId) || null;

  const selectWine = (wineId) => {
    setSelectedId(wineId);
    const el = document.getElementById(`bottle-${wineId}`);
    const container = carouselRef.current;
    if (el && container) {
      // Centrar horizontalmente a mano (en vez de scrollIntoView) para que
      // nunca arrastre scroll vertical de la pagina - solo mueve el carrusel.
      const target = el.offsetLeft - (container.clientWidth - el.clientWidth) / 2;
      container.scrollTo({ left: target, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <div className={styles.status}>Cargando mapa...</div>;
  }

  if (error) {
    return <div className={styles.status}>Error: {error}</div>;
  }

  if (!category) {
    return <div className={styles.status}>Categoría no encontrada</div>;
  }

  const bodyLevel = selectedWine ? bucket4(selectedWine.intensity) : 0;

  return (
    <div className={styles.page}>
      {/* Misma barra de categorias que el resto del menu: sin ella, el mapa
          era un callejon sin salida para seguir navegando. */}
      <SmallNavBar />
      <header className={styles.header}>
        <Link to="/Home" className={styles.backButton}>
          <svg fill="none" height="20" stroke="currentColor" viewBox="0 0 24 24" width="20">
            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
          Volver
        </Link>
        <div className={styles.badge}>RUSSAFA · CARTA VIVA</div>
      </header>

      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>MAPA DE</h1>
        <h2 className={`${styles.heroTitle} ${styles.heroAccent}`}>SABORES</h2>
        <div className={styles.heroBar} />
      </section>

      <section className={styles.quickMatchSection}>
        <div className={styles.quickMatchLabel}>
          <span>✦</span> ¿Qué te apetece hoy?
        </div>
        <div className={styles.quickMatchRow}>
          {QUICK_MATCHES.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`${styles.quickMatchButton} ${quickMatch === m.id ? styles.quickMatchButtonActive : ''}`}
              onClick={() => {
                setQuickMatch(m.id);
                setTypeFilter('all');
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.funkSection}>
        <div className={styles.funkHeader}>
          <span>⚡ Nivel funk mínimo</span>
          <span className={styles.funkIndicator}>
            {minFunk === 1 ? 'TODOS (1-4)' : `Nivel ≥ ${minFunk}/4`}
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="4"
          step="1"
          value={minFunk}
          onChange={(e) => setMinFunk(Number(e.target.value))}
          className={styles.funkRange}
        />
      </section>

      <section className={styles.filters}>
        {WINE_TYPE_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`${styles.filterButton} ${typeFilter === filter.id ? styles.filterButtonActive : ''}`}
            onClick={() => setTypeFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </section>

      <main className={styles.carouselSection}>
        <div className={styles.carouselHeader}>
          <span>🍾 Botellas en mesa</span>
          <span className={styles.carouselCount}>{visibleWines.length} vinos</span>
        </div>
        <div className={styles.carouselWrap}>
          {visibleWines.length === 0 ? (
            <p className={styles.empty}>No hay vinos en este filtro.</p>
          ) : (
            <div className={styles.carousel} ref={carouselRef}>
              {visibleWines.map((wine) => {
                const isSelected = wine.id === selectedId;
                return (
                  <button
                    key={wine.id}
                    id={`bottle-${wine.id}`}
                    type="button"
                    className={`${styles.bottleCard} ${isSelected ? styles.bottleCardActive : ''}`}
                    onClick={() => selectWine(wine.id)}
                  >
                    <div className={styles.bottleImageWrap}>
                      {wine.image?.small ? (
                        <img src={wine.image.small} alt={wine.name} className={styles.bottleImage} />
                      ) : (
                        <div className={styles.bottleImagePlaceholder}>🍷</div>
                      )}
                      <div className={styles.bottleGradient} />
                      <span className={`${styles.bottleBadge} ${TYPE_BADGE_CLASS[wine.type] || styles.badgeBlanco}`}>
                        {TYPE_LABEL[wine.type] || wine.type.toUpperCase()}
                      </span>
                      <span className={styles.bottlePrice}>{formatPrice(wine.price)}</span>
                    </div>
                    <div className={styles.bottleContent}>
                      <h4 className={styles.bottleName}>{wine.name}</h4>
                      <div className={styles.bottleMeta}>
                        <span className={styles.funkBolts}>
                          {[1, 2, 3, 4].map((i) => (
                            <span key={i} className={i <= wine.funkiness ? styles.boltOn : styles.boltOff}>⚡</span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <section className={styles.cardSection}>
        {selectedWine ? (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardThumbWrap}>
                {selectedWine.image?.small ? (
                  <img src={selectedWine.image.small} alt={selectedWine.name} className={styles.cardThumb} />
                ) : (
                  <div className={styles.cardThumbPlaceholder}>🍷</div>
                )}
              </div>
              <div className={styles.cardTitleWrap}>
                <span className={`${styles.cardTypeBadge} ${TYPE_BADGE_CLASS[selectedWine.type] || styles.badgeBlanco}`}>
                  {TYPE_LABEL[selectedWine.type] || selectedWine.type.toUpperCase()}
                </span>
                <h3 className={styles.cardTitle}>{selectedWine.name}</h3>
                {/* Precio debajo del nombre: al lado le quitaba ancho y los
                    nombres largos se partian a mitad de palabra. */}
                <div className={styles.priceTag}>
                  {formatPrice(selectedWine.price)}
                </div>
              </div>
            </div>

            <div className={styles.bars}>
              <Meter label="Nivel funk" value={selectedWine.funkiness} max={4} text={`${selectedWine.funkiness}/4`} fillClass={styles.barFunk} />
              <Meter label="Acidez" value={selectedWine.acidity} max={5} text={`${selectedWine.acidity}/5`} fillClass={styles.barAcidity} />
            </div>

            <div className={styles.bodyTag}>
              <span className={styles.bodyTagLabel}>Cuerpo</span>
              <span className={styles.bodyTagValue}>{bodyLabel(bodyLevel)}</span>
            </div>

            {selectedWine.description && (
              <div className={styles.tastingNote}>
                <div className={styles.tastingNoteLabel}>✦ Nota de cata</div>
                <p>{selectedWine.description}</p>
              </div>
            )}

            {selectedWine.pairingSuggestion && (
              <div className={styles.pairing}>
                <span className={styles.pairingLabel}>🍽️ Marida bien con:</span>
                <span className={styles.pairingTag}>{selectedWine.pairingSuggestion}</span>
              </div>
            )}
          </div>
        ) : (
          <p className={styles.empty}>No hay vinos en este filtro.</p>
        )}
      </section>
    </div>
  );
};

const Meter = ({ label, value, max, text, fillClass }) => (
  <div className={styles.meter}>
    <div className={styles.meterHeader}>
      <span className={styles.barLabel}>{label}</span>
      <span className={styles.meterText}>{text}</span>
    </div>
    <div className={styles.barRow}>
      {Array.from({ length: max }, (_, i) => i + 1).map((level) => (
        <div
          key={level}
          className={`${styles.barSegment} ${level <= value ? fillClass : ''}`}
        />
      ))}
    </div>
  </div>
);

export default WineFlavorMap;
